import { useEffect, useCallback, useRef } from "react";
import { useDialog } from "@repo/ui";

type LegacyProps = {
  pendingChanges: Record<number, unknown>;
  onDiscard: () => void;
  onConfirmed?: never;
};

type BooleanProps = {
  hasUnsavedChanges: boolean;
  onDiscard: () => void;
  onConfirmed?: never;
  pendingChanges?: never;
};

type Props = LegacyProps | BooleanProps;

export const useUnsavedChangesWarning = (props: Props) => {
  const { showDialog, closeDialog } = useDialog();

  const hasChanges =
    "pendingChanges" in props && props.pendingChanges !== undefined
      ? Object.keys(props.pendingChanges).length > 0
      : ((props as BooleanProps).hasUnsavedChanges ?? false);

  const changeLabel =
    "pendingChanges" in props && props.pendingChanges !== undefined
      ? `${Object.keys(props.pendingChanges).length} unsaved change(s)`
      : "unsaved changes";

  const showDiscardDialog = useCallback(
    (onConfirmed?: () => void) => {
      if (!hasChanges) {
        onConfirmed?.();
        return;
      }

      showDialog({
        title: "Discard Changes?",
        description: `You have ${changeLabel}. Are you sure you want to discard them?`,
        confirmText: "Discard Changes",
        confirmVariant: "destructive",
        cancelText: "Keep Editing",
        cancelVariant: "outline",
        disableXClose: true,
        onConfirm: () => {
          props.onDiscard();
          closeDialog();
          onConfirmed?.();
        },
      });
    },
    [hasChanges, changeLabel, showDialog, closeDialog, props.onDiscard]
  );

  return { showDiscardDialog };
};

export const useClickInterception = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  pendingChanges: Record<number, unknown> | boolean,
  showDiscardDialog: (onConfirmed?: () => void) => void,
  onAfterDiscard?: () => void
) => {
  const bypassNextClick = useRef(false);

  useEffect(() => {
    const hasPendingChanges =
      typeof pendingChanges === "boolean" ? pendingChanges : Object.keys(pendingChanges).length > 0;

    if (!hasPendingChanges) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (bypassNextClick.current) {
        bypassNextClick.current = false;
        return;
      }

      if (containerRef.current?.contains(target)) return;

      const isInteractiveElement =
        target.closest("button") ||
        target.closest("a") ||
        target.closest('[role="button"]') ||
        target.closest('[role="tab"]') ||
        target.closest('[role="menuitem"]') ||
        target.closest("tr") ||
        target.closest("[data-dialog-backdrop]");

      if (!isInteractiveElement) return;

      // Allow navigation-safe elements (e.g. discard dialog's own buttons)
      if (target.closest("[data-allow-navigation]")) return;

      // Allow backdrop of the discard dialog (disableXClose)
      if (target.closest("[data-dialog-no-close]")) return;

      event.preventDefault();
      event.stopPropagation();

      if (onAfterDiscard) {
        showDiscardDialog(onAfterDiscard);
      } else {
        showDiscardDialog(() => {
          bypassNextClick.current = true;
          (event.target as HTMLElement)?.click();
        });
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => document.removeEventListener("click", handleClickOutside, true);
  }, [containerRef, pendingChanges, showDiscardDialog, onAfterDiscard]);
};

export const useBeforeUnloadWarning = (pendingChanges: Record<number, unknown>) => {
  useEffect(() => {
    const hasPendingChanges = Object.keys(pendingChanges).length > 0;
    if (!hasPendingChanges) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [pendingChanges]);
};
